import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Typography from "@material-ui/core/Typography";
import ButtonComponent from "../common/ButtonComponent";
import Link from "@material-ui/core/Link";
import { useIntl, defineMessage, defineMessages } from "react-intl";
import TextFieldComponent from "../common/TextField";
import AppState from "../../store/AppState";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { CloseModal, setModalState } from "../../store/modal/ModalActions";
import {
  closeFindProductsSubscriptionsAndResetState,
  queryProductForFindProducts,
  updateFindProductsProgressCache,
} from "../../store/find-products/FindProductsActions";
import {
  selectIsSubmitted,
  selectIsSubmitting,
  selectFindProductsLoadingPercentage,
  selectIsFetchingProductIds,
  selectIsFindProductsAssetsLoading,
  selectGetProductIds,
} from "../../store/find-products/FindProductSelectors";
import AppEnv from "../../utils/AppEnv";
import FindProductCatalogSelection from "./FindProductsCatalogSelection";
import FindProductsCategorySelection from "./FindProductsCategorySelection";
import FindProductsProductList from "./FindProductsProductList";
import {
  styles,
  StyledDiv,
  StyledFormControl,
  styleClasses,
} from "./FindProductsModalStyles";
import { UpdateProductData } from "../../store/find-products/FindProductsTypes";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../../store/store-list/StoreListSelectors";
import { isAddingRemovingProductIdsList } from "../../store/product-list/ProductListSelectors";
import CustomDialog from "../common/DialogContainer";
import { Box, useTheme } from "@mui/material";
import ProgressBar from "../common/ProgressBar";
import DropdownMenu from "../common/DropdownMenu";
import { selectConfigValue } from "../../store/app-config/AppConfigSelectors";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";

const text = defineMessages({
  modalTitle: {
    id: "findProductModal.modalTitle",
    defaultMessage: "Find Products",
  },
  saveAction: {
    id: "findProductModal.saveActionLabel",
    defaultMessage: "Save",
  },
  closeAction: {
    id: "findProductModal.closeActionLabel",
    defaultMessage: "Close",
  },
  selectAll: {
    id: "findProductModal.selectAllLabel",
    defaultMessage: "Select All",
  },
  deselectAll: {
    id: "findProductModal.deselectAllLabel",
    defaultMessage: "Deselect All",
  },
  reset: {
    id: "findProductModal.resetLabel",
    defaultMessage: "Reset",
  },
  sortBy: {
    id: "findProductModal.sortByLabel",
    defaultMessage: "Sort By:",
  },
  search: {
    id: "findProductModal.searchPlaceholder",
    defaultMessage: "search by product name or sku",
  },
  searchRequired: {
    id: "findProductModal.searchRequired",
    defaultMessage: "Please enter a search term",
  },
  searchLabel: {
    id: "findProductModal.searchLabel",
    defaultMessage: "Search",
  },
});

const sortingRules = [
  {
    name: defineMessage({
      id: "findProductModal.sortingRulesByNameText",
      defaultMessage: "Name",
    }),
    value: "name",
  },
  {
    name: defineMessage({
      id: "findProductModal.sortingRulesByPartNumberText",
      defaultMessage: "Part Number",
    }),
    value: "part",
  },
];

interface Props {
  categoryId: string;
}

const FindProductsModal: React.FC<Props> = (props) => {
  const intl = useIntl();
  const theme = useTheme();
  const {
    determinateProgressWrap,
    linearProgressRow,
    linearProgressTrack,
    linearProgressPercentLabel,
    sortingLink,
    hidden,
  } = styles(theme);
  const dispatch = useDispatch();
  const [selectedCatalog, setSelectedCatalog] = useState("");
  const [selected, setSelected] = useState([] as string[]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortingValue, setSortingValue] = useState(sortingRules[0].value);
  const storeId = useSelector(selectCurrentStoreId) ?? "";
  const localeId = useSelector(selectCurrentLocale) ?? "";

  const [productsToBeAdded, setProductsToBeAdded] = useState(
    {} as UpdateProductData,
  );
  const [productsToBeRemoved, setProductsToBeRemoved] = useState(
    {} as UpdateProductData,
  );

  const advancedFindEnabled =
    useSelector((state: AppState) =>
      selectConfigValue(state, "advancedFindEnabled"),
    ) === "true";

  const adapterInfo: AdapterInfoObject = useSelector((state: AppState) =>
    selectAdapterInfo(state),
  );
  const isProductSearchEnabled =
    adapterInfo?.operations?.includes("POST /v1/{accountId}/product-query") ??
    false;

  const [currentModalSelectedCategory, setCurrentModalSelectedCategory] =
    useState("");
  const [page, setPage] = useState(1);
  const pageSize: number = +(AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24);

  const isSubmitting = useSelector((state: AppState) =>
    selectIsSubmitting(state),
  );
  const isSubmitted = useSelector((state: AppState) =>
    selectIsSubmitted(state),
  );

  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isFindProductsAssetsLoading = useSelector(
    selectIsFindProductsAssetsLoading,
  );
  const findProductsProductIds = useSelector(selectGetProductIds);

  const findProductsProgress = useSelector((state: AppState) =>
    selectFindProductsLoadingPercentage(state, page, pageSize),
  );

  const findProductsProgressPercent = Number.isFinite(findProductsProgress)
    ? Math.min(100, Math.max(0, findProductsProgress))
    : 0;

  const showFindProductsDeterminateProgress =
    !isFetchingProductIds &&
    findProductsProductIds.length > 0 &&
    (isFindProductsAssetsLoading || findProductsProgressPercent < 100);

  useEffect(() => {
    if (findProductsProgress > 0 && findProductsProgress < 100) {
      dispatch(updateFindProductsProgressCache(page, findProductsProgress));
    }
  }, [findProductsProgress, page, dispatch]);

  useEffect(() => {
    setPage(1);
  }, [currentModalSelectedCategory]);

  useEffect(() => {
    if (isSubmitted && !isSubmitting) {
      setProductsToBeAdded({});
      setProductsToBeRemoved({});
    }
  }, [isSubmitted, isSubmitting]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const existingAddingRemoveProductIdsOperation = useSelector(
    isAddingRemovingProductIdsList,
  );

  const [isErrorOnSearch, setIsErrorOnSearch] = useState(false);
  const categoryClickRef = useRef(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, "FindProductsModal"),
  );
  const clearSearchForm = () => {
    setSearchTerm("");
    setIsErrorOnSearch(false);
  };

  const handleClose = () => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    dispatch(closeFindProductsSubscriptionsAndResetState(selectedCatalog));
    setSelected([]);
    setSelectedCatalog("");
    clearSearchForm();
    setSortingValue(sortingRules[0].value);
    setProductsToBeAdded({});
    setProductsToBeRemoved({});
    setCurrentModalSelectedCategory("");
    setPage(1);
    dispatch(setModalState("FindProductsModal", false));
    dispatch(CloseModal({}));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    if (searchTerm.trim().length === 0) {
      setIsErrorOnSearch(true);
      return;
    }
    setIsErrorOnSearch(false);
    if (isProductSearchEnabled) {
      setPage(1);
      dispatch(
        queryProductForFindProducts({
          search: searchTerm,
          categoryId: currentModalSelectedCategory || undefined,
          catalogId: selectedCatalog,
          localeCode: localeId,
          storeId: storeId,
        }),
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default form submission
      handleSearchSubmit(); // Call search function directly
    }
  };

  const handleBlur = () => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Add a small delay so mousedown on catalog / product list / etc. can set
    // categoryClickRef before we decide whether to auto-submit search.
    blurTimeoutRef.current = setTimeout(() => {
      if (!categoryClickRef.current && searchTerm.trim().length > 0) {
        handleSearchSubmit();
      }
      categoryClickRef.current = false;
    }, 150);
  };

  /** Set when mousedown happens on modal UI that should not trigger blur-driven search. */
  const handleInteractiveElementMouseDown = () => {
    categoryClickRef.current = true;
  };
  const showCategoryList = useMemo(() => {
    return selectedCatalog !== "";
  }, [selectedCatalog]);

  function FindProductsLinearProgressWithLabel(props: { value: number }) {
    return (
      <Box sx={linearProgressRow}>
        <Box sx={linearProgressTrack}>
          <ProgressBar variant="determinate" value={props.value} />
        </Box>
        <Box sx={linearProgressPercentLabel}>
          <Typography variant="body2" color="textPrimary">
            {`${Number.isFinite(props.value) ? Math.round(props.value) : 0}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  const findProductsModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage(text.modalTitle)}
      </Typography>
    );
  };

  const findProductsModalContent = () => {
    return (
      <StyledDiv
        onMouseDown={handleInteractiveElementMouseDown}
        onMouseDownCapture={handleInteractiveElementMouseDown}
      >
        {isFetchingProductIds && <ProgressBar />}
        {showFindProductsDeterminateProgress ? (
          <Box sx={determinateProgressWrap}>
            <FindProductsLinearProgressWithLabel
              value={findProductsProgressPercent}
            />
          </Box>
        ) : null}
        <StyledDiv>
          <FindProductCatalogSelection
            localeId={localeId}
            storeId={storeId}
            setCatalog={setSelectedCatalog}
            catalog={selectedCatalog}
            setCurrentModalSelectedCategory={setCurrentModalSelectedCategory}
          />
        </StyledDiv>
        {advancedFindEnabled &&
          isProductSearchEnabled &&
          selectedCatalog !== "" && (
            <StyledDiv className={styleClasses.main}>
              <StyledDiv className={styleClasses.search}>
                <TextFieldComponent
                  textFieldId="SearchInput"
                  label={intl.formatMessage(text.searchLabel)}
                  value={searchTerm}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                  handleBlur={handleBlur}
                  placeholderText={intl.formatMessage(text.search)}
                  error={isErrorOnSearch}
                  helperText={
                    isErrorOnSearch
                      ? intl.formatMessage(text.searchRequired)
                      : ""
                  }
                  classes={{
                    root: styleClasses.inputRoot,
                    input: styleClasses.inputInput,
                  }}
                />
              </StyledDiv>
              <StyledDiv className={styleClasses.sortingHidden}>
                <Link style={sortingLink}>
                  {intl.formatMessage(text.selectAll)}
                </Link>
                <Link style={sortingLink}>
                  {intl.formatMessage(text.deselectAll)}
                </Link>
                <Link style={sortingLink}>
                  {intl.formatMessage(text.reset)}
                </Link>
              </StyledDiv>
              <Box sx={hidden}>
                <StyledFormControl className={styleClasses.formControl}>
                  <label id={"find-product-select-sorting-label"}>
                    {" "}
                    {intl.formatMessage(text.sortBy)}
                    {": "}
                  </label>
                  <DropdownMenu
                    menuId="FindProductsModal"
                    value={sortingValue}
                    sortingRules={sortingRules}
                  />
                </StyledFormControl>
              </Box>
            </StyledDiv>
          )}

        {showCategoryList && (
          <StyledDiv className={styleClasses.catagoryProductList}>
            <StyledDiv className={styleClasses.leftPanel}>
              <FindProductsCategorySelection
                storeId={storeId}
                localeId={localeId}
                catalog={selectedCatalog}
                checked={true}
                setSelected={setSelected}
                selected={selected}
                orderBy={sortingValue}
                clearSearchForm={clearSearchForm}
                setCurrentModalSelectedCategory={
                  setCurrentModalSelectedCategory
                }
                searchTerm={searchTerm}
                advancedFindEnabled={advancedFindEnabled}
                isProductSearchEnabled={isProductSearchEnabled}
              />
            </StyledDiv>
            <StyledDiv className={styleClasses.productList}>
              <FindProductsProductList
                productsToBeAdded={productsToBeAdded}
                setProductsToBeAdded={setProductsToBeAdded}
                productsToBeRemoved={productsToBeRemoved}
                setProductsToBeRemoved={setProductsToBeRemoved}
                currentModalSelectedCategory={currentModalSelectedCategory}
                existingAddingRemoveProductIdsOperation={
                  existingAddingRemoveProductIdsOperation
                }
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            </StyledDiv>
          </StyledDiv>
        )}
        {/* </StyledDiv> */}
      </StyledDiv>
    );
  };

  const dialogActions = () => {
    return (
      <StyledDiv
        className={styleClasses.btnClose}
        onMouseDown={handleInteractiveElementMouseDown}
      >
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleClose}
        >
          {intl.formatMessage(text.closeAction)}
        </ButtonComponent>
      </StyledDiv>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={() => handleClose()}
      title={findProductsModalTitle()}
      fullWidth
      maxWidth={"lg"}
      actions={dialogActions()}
    >
      {findProductsModalContent()}
    </CustomDialog>
  );
};

export default FindProductsModal;
